import {DestroyRef, Directive, ElementRef, inject, NgZone} from '@angular/core';
import {NgDropdownPanelComponent, NgSelectComponent} from '@ng-select/ng-select';
import {scrollParentsOf} from '../_shared/dom/scroll-parent';

/**
 * Slack allowed before the input counts as clipped, in CSS pixels.
 *
 * Exists purely to absorb sub-pixel noise — fractional layout and non-integer `devicePixelRatio` leave rects a
 * few hundredths of a pixel outside their container on a page that is not moving at all. Without it the panel
 * would toggle visibility while standing still. It is deliberately far too small to tolerate a real clip.
 */
const EDGE_TOLERANCE_PX = 1;

/**
 * Is this element the page's own scrolling root, rather than a scroll box inside the page?
 *
 * Both `<html>` and `<body>` can be it depending on document mode, and `document.scrollingElement` names
 * whichever one currently is — all three are checked because `scrollParentsOf` walks the DOM and will happily
 * hand back `<body>` on a page whose root scroller is `<html>`, and vice versa.
 */
function isDocumentScrollRoot(element: Element): boolean {
  return element === document.scrollingElement
    || element === document.documentElement
    || element === document.body;
}

/**
 * Keeps an `<ng-select>` dropdown panel glued to its input while an ancestor scrolls — the missing half of
 * `[appendTo]` (Bug #236).
 *
 * ## Why this has to exist
 *
 * `ng-select` writes the panel's position **once, at open**, as inline pixel offsets measured against the
 * `appendTo` element. It then registers exactly three listeners (`ng-select-ng-select.mjs:1637-1643`): a
 * `scroll` listener on the panel's OWN inner scroll host (virtual scrolling), an outside-`click` listener, and
 * a `mousedown` listener on the panel. There is **no ancestor-scroll listener, no `resize` listener and no
 * Intersection/Resize/Mutation observer anywhere in the library**, and `blur` does not close the panel either.
 *
 * So `appendTo` on its own is a regression, not a fix: the whole point of the target is that it sits OUTSIDE
 * the clipping scroll container, which means the panel no longer moves when that container scrolls. The user
 * sees the option list stay nailed to the page while the input it belongs to slides away — the work item calls
 * that "worse than a clipped one", and it is. `appendTo` must therefore never ship without this directive.
 *
 * ## What it does
 *
 * While the select is open it listens — **outside the Angular zone**, passively — on every scrolling ancestor
 * (resolved from computed overflow, see {@link scrollParentsOf}), on `document` for page scroll, and on
 * `window` for `resize`. On each event it either
 *
 *  * re-runs `NgDropdownPanelComponent.adjustPosition()` (public API) so the panel follows the input, or
 *  * **hides** the panel once the edge it HANGS OFF goes under a clipper, restoring it when that edge comes
 *    back, or
 *  * closes the select, when the input has scrolled entirely out of one of those boxes — a panel pointing at
 *    an input nobody can see is noise, and this is what a native `<select>` does too.
 *
 * The middle state is the point, and it is asymmetric on purpose. A dropdown reads as belonging to its input
 * because the two visibly join along one edge: the input's bottom when the panel opens downwards, its top when
 * ng-select has flipped it up. Hide that edge and the panel is left hovering over unrelated content past the
 * clipper — but hide the FAR edge and the join is still on screen and still correct, so the panel should stay
 * put right up until the input is gone entirely. Hiding is reversible and costs the user nothing: the select
 * stays open, so the marked option and any typed search text survive. See {@link inputVisibility}.
 *
 * ## Horizontal scroll — closes, never follows
 *
 * `adjustPosition()` calls only the private `_updateYPosition()`; `_updateXPosition()` is not reachable from
 * outside the library, so a *horizontally* scrolling ancestor moves the input sideways and the panel cannot be
 * made to follow. This is exactly the extension the original version of this file predicted would be needed:
 * `shift-maker` turned out to have one (the `overflow-x-auto` strip that wraps its event columns, #236 rows
 * #15/#16), so {@link inputVisibility} bounds BOTH axes: a sideways cut hides the panel and a sideways exit
 * closes it. Vertically the panel still follows until it is cut; only horizontally does it give up entirely,
 * because giving up is the only honest option through the public API.
 *
 * ## Applying it
 *
 * The primary selector is `ng-select[appendTo]`, so the coupling is mechanical: any select in a component that
 * imports this directive gets the scroll handling for free the moment it is portaled, and nobody can add an
 * `appendTo` and forget the handler. `ng-select[cqScrollAwareDropdown]` is the explicit opt-in for a
 * NON-portaled select that should still close when it scrolls away.
 *
 * ```html
 * <ng-select appendTo="#composition-editor-panel" …>
 * ```
 * ```ts
 * imports: [NgSelectComponent, NgOptionComponent, NgSelectScrollAwareDirective]
 * ```
 */
@Directive({
  selector: 'ng-select[appendTo], ng-select[cqScrollAwareDropdown]'
})
export class NgSelectScrollAwareDirective {
  /** The select this directive decorates. `self` — it is always on the same element, never an ancestor's. */
  private readonly select = inject(NgSelectComponent, {self: true});
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly zone = inject(NgZone);

  /** Removes the listeners registered by {@link attach}; `null` while the select is closed. */
  private teardown: (() => void) | null = null;

  /**
   * The scrolling ancestors for the CURRENT open cycle, resolved once in {@link attach}.
   *
   * Cached rather than re-walked per event on purpose: {@link scrollParentsOf} calls `getComputedStyle` on
   * every ancestor, and this runs on every frame of a scroll gesture. The DOM above an open dropdown does not
   * restructure mid-scroll, so one walk per open is both correct and cheap.
   */
  private scrollports: HTMLElement[] = [];

  constructor() {
    // `(open)` / `(close)` rather than a signal effect: they are the component's documented public API, they
    // fire for programmatic `open()`/`close()` as well as user interaction, and an effect would additionally
    // have to be scheduled through change detection before the listeners went up.
    const subscriptions = [
      this.select.openEvent.subscribe(() => this.attach()),
      this.select.closeEvent.subscribe(() => this.detach())
    ];
    // The panel is destroyed with the select, but the listeners live on foreign elements (the scroll parents,
    // document, window) and would outlive it — a leak, and a handler reaching into a dead component.
    inject(DestroyRef).onDestroy(() => {
      for (const subscription of subscriptions) subscription.unsubscribe();
      this.detach();
    });
  }

  /**
   * Registers the viewport listeners for one open cycle.
   *
   * The scroll parents are resolved HERE and not once in the constructor: the select may be inside an `@if` /
   * `@for` whose ancestors changed, a slide-over that was `display:none` at construction time (computed
   * overflow of a hidden box is still readable, but the box itself may not have existed), or a page that has
   * since re-laid out.
   */
  private attach(): void {
    this.detach();

    const onViewportChange = () => this.follow();
    this.scrollports = scrollParentsOf(this.host);
    // `scroll` does not bubble from elements, so EVERY scrolling ancestor needs its own listener — in the
    // composition editor both `#component-container` and the office shell's region move the input. Page scroll
    // is delivered on `document` (not on `document.scrollingElement`), hence the extra target.
    const targets: EventTarget[] = [...this.scrollports, document];

    this.zone.runOutsideAngular(() => {
      for (const target of targets) target.addEventListener('scroll', onViewportChange, {passive: true});
      window.addEventListener('resize', onViewportChange, {passive: true});
    });

    this.teardown = () => {
      for (const target of targets) target.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
    };
  }

  /** Idempotent — safe to call on close, on destroy, and defensively from the handler itself. */
  private detach(): void {
    this.teardown?.();
    this.teardown = null;
    this.scrollports = [];
    // Never hand back a panel we hid. ng-select destroys the panel on close, so this is belt-and-braces for the
    // paths where it does not (a detach that races the close, or attach()'s own defensive detach) — a panel
    // that reopened stuck at `visibility: hidden` would look exactly like the select being broken.
    this.setPanelHidden(false);
  }

  /**
   * One viewport-change tick: close if the input has left its scrollport, hide the panel if the input is only
   * partly inside it, otherwise re-place the panel and make sure it is visible.
   *
   * Runs outside the Angular zone. `adjustPosition()` is a pure inline-style write and must stay there — it can
   * fire on every animation frame of a scroll and has no business triggering change detection. `close()` does
   * mutate component state, so it is hopped back into the zone explicitly.
   */
  private follow(): void {
    // A stray event after the panel went away (close and teardown are not atomic across event dispatch).
    if (!this.select.isOpen()) {
      this.detach();
      return;
    }

    const visibility = this.inputVisibility();

    if (visibility === 'gone') {
      this.zone.run(() => this.select.close());
      return;
    }

    // Guard on `appendTo`: `adjustPosition()` → `_updateYPosition()` dereferences the panel's `_parent`, which
    // the library only assigns inside `_appendDropdown()` — i.e. only when appendTo is set. Calling it on a
    // non-portaled panel throws. A non-portaled panel is a DOM sibling of its input anyway and needs no help.
    if (!this.select.appendTo()) return;

    // Reposition even while hidden, so the panel is already in the right place the instant it is revealed
    // again — revealing and re-measuring in the same frame would paint one frame at the stale offset.
    (this.select.dropdownPanel() as NgDropdownPanelComponent | undefined)?.adjustPosition();
    this.setPanelHidden(visibility === 'clipped');
  }

  /**
   * Shows or hides the panel WITHOUT closing the select.
   *
   * `visibility` rather than `display`: it keeps the panel's box measurable, so the `adjustPosition()` above
   * still computes real offsets while hidden, and it takes the panel out of the accessibility tree for as long
   * as it is invisible — which `opacity` would not. The select stays open throughout, so the marked option and
   * any typed search text survive the scroll.
   *
   * The panel is found by `dropdownId` (the id `aria-controls` already points at) because
   * `NgDropdownPanelComponent` exposes no public element accessor, and a bare `.ng-dropdown-panel` query would
   * be ambiguous the moment a second select is open.
   */
  private setPanelHidden(hidden: boolean): void {
    const panel = document.getElementById(this.select.dropdownId);
    if (panel) panel.style.visibility = hidden ? 'hidden' : '';
  }

  /**
   * How much of the input is inside EVERY one of its scrollports (and the viewport)?
   *
   *  * `full` — entirely inside all of them; the panel belongs on screen.
   *  * `clipped` — overlapping, but the edge the panel HANGS OFF has gone under a clipper. The panel is HIDDEN
   *    rather than closed: mid-scroll the input is on its way somewhere, and a panel whose join to the input is
   *    no longer on screen hovers over whatever unrelated content sits past the clipper's edge. Hiding is
   *    reversible — closing would throw away the user's marked option and typed filter over a few pixels of
   *    scroll. Note this is deliberately NOT "the input is not fully visible": cutting the FAR edge (the top of
   *    the input, for a panel that opens downwards) leaves the join intact and on screen, and the panel stays.
   *  * `gone` — no overlap at all. Here the select really does close, which bounds the open state: otherwise a
   *    panel could sit invisible-but-open indefinitely and spring back when the user returned to that part of
   *    the page.
   *
   * A SCROLL BOX cuts on both axes: vertically the panel can follow the input until it is cut, horizontally it
   * can never follow at all (`_updateXPosition()` is private — see the class docs), so a sideways cut hides it
   * just the same. The VIEWPORT, by contrast, cuts only vertically. An input wider than the screen is ordinary
   * responsive layout rather than a scroll artefact, and requiring horizontal containment against the viewport
   * hides the panel the instant it opens on a narrow screen — permanently, since no amount of scrolling makes
   * a 416px control fit a 390px viewport. That is a far worse bug than the one being fixed.
   *
   * A document-level scroll root (`<html>`/`<body>`) is measured as the VIEWPORT, never by its own
   * `getBoundingClientRect()`. Its border box is not its scrollport: it can be far taller than the screen, or —
   * when the page's content is positioned out of flow — far SHORTER, at which point strict containment against
   * it rejects an input that is plainly on screen. The weaker "no overlap at all" test never noticed, which is
   * why this only surfaced once the panel started hiding on partial clipping.
   */
  private inputVisibility(): 'full' | 'clipped' | 'gone' {
    const input = this.host.getBoundingClientRect();
    const viewport = new DOMRect(0, 0, window.innerWidth, window.innerHeight);

    // A real scroll box clips on both axes. The viewport clips only VERTICALLY — an input wider than the
    // screen is ordinary responsive layout, not a scroll artefact, and treating it as clipped would hide the
    // panel permanently at phone width, which is far worse than the bug being fixed. A document-level scroll
    // root IS the viewport and inherits that rule.
    const clippers = this.scrollports.map(parent => isDocumentScrollRoot(parent)
      ? {box: viewport, clipsHorizontally: false}
      : {box: parent.getBoundingClientRect(), clipsHorizontally: true});
    clippers.push({box: viewport, clipsHorizontally: false});

    const gone = clippers.some(({box}) =>
      input.bottom <= box.top || input.top >= box.bottom
      || input.right <= box.left || input.left >= box.right);
    if (gone) return 'gone';

    // Only the ANCHORED edge counts vertically. The panel hangs off one edge of the input — its bottom when it
    // opens downwards, its top when ng-select has flipped it up — and that join is what the eye reads as "this
    // list belongs to that input". Cover the far edge and the join is still on screen and still correct, so the
    // panel stays; cover the anchored edge and the panel is left pointing at nothing, so it goes.
    const anchorY = this.anchorEdge() === 'top' ? input.top : input.bottom;
    const anchorVisible = (box: DOMRect) =>
      anchorY >= box.top - EDGE_TOLERANCE_PX && anchorY <= box.bottom + EDGE_TOLERANCE_PX;
    // Horizontally there is no equivalent choice: the panel spans the input's whole width and cannot follow it
    // sideways at all (`_updateXPosition()` is private), so any horizontal cut by a real scroll box hides it.
    const insideHorizontally = (box: DOMRect) =>
      input.left >= box.left - EDGE_TOLERANCE_PX && input.right <= box.right + EDGE_TOLERANCE_PX;

    const clipped = clippers.some(({box, clipsHorizontally}) =>
      !anchorVisible(box) || (clipsHorizontally && !insideHorizontally(box)));

    return clipped ? 'clipped' : 'full';
  }

  /**
   * Which edge of the input the panel is currently attached to.
   *
   * Primary source is the panel's public `currentPosition` — ng-select's RESOLVED side, which auto-flips upward
   * when there is no room below, so the configured `dropdownPosition` is not necessarily what is on screen.
   *
   * It is only assigned in the library's `_handleDropdownPosition()`, which runs once when the panel is first
   * placed — NOT in `adjustPosition()`. So there is a window just after `open()` where it is still `undefined`,
   * and a scroll landing in that window would otherwise be judged against the wrong edge. The fallback asks the
   * DOM the same question the API was asked: a panel sitting entirely above the input is top-anchored.
   */
  private anchorEdge(): 'top' | 'bottom' {
    const position = (this.select.dropdownPanel() as NgDropdownPanelComponent | undefined)?.currentPosition;
    if (position === 'top' || position === 'bottom') return position;

    const panel = document.getElementById(this.select.dropdownId);
    if (!panel) return 'bottom';
    return panel.getBoundingClientRect().bottom <= this.host.getBoundingClientRect().top + EDGE_TOLERANCE_PX
      ? 'top'
      : 'bottom';
  }
}
